import { Error } from '@mui/icons-material'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { Field, FieldProps, FormikValues, useFormikContext } from 'formik'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SwipeableViews from 'react-swipeable-views'
import { VinciFormModal } from '~/components/'
import { ProtectedComponent } from '~/components/ProtectedComponent'
import { VinciAutocomplete } from '~/components/VinciAutocomplete'
import { useAuth } from '~/hooks/useAuth'
import { useFetch } from '~/hooks/useFetch'
import useRequestWithToast from '~/hooks/useRequestWithToast'
import { createCompany } from '~/modules/company/data/companyApi'
import { checkIsManager } from '~/modules/inspection/utils/checkIsManager'
import { ICompany, IIdentity, IUser, profileOptions, statusOptions } from '~/utils/types'
import { CompanyForm } from '~company/components/CompanyForm'
import { companyValidationSchema } from '~company/utils/validationSchema'
import { IdentitySelector } from './IdentitySelector/IdentitySelector'
import * as S from './UserForm.styles'

export const UserForm = ({ createGuestOnly }: { createGuestOnly?: boolean }) => {
  const { request } = useRequestWithToast()
  const formik = useFormikContext<IUser & { country_code: string }>()
  const { t } = useTranslation()

  const [currentTab, setCurrentTab] = useState(0)

  const { user: loggedInUser } = useAuth()

  const isEditing = useMemo(() => Boolean(formik.values?._id), [formik])
  const [companyModal, setCompanyModal] = useState<string | boolean>(false)
  const { data, loading, mutate } = useFetch<ICompany[]>('company')

  const handleSaveCompany = async (values: FormikValues) => {
    const response = await request<ICompany>({
      initialText: t('Creating company...'),
      errorText: t('There was an error creating company. Please try again!'),
      successText: t('Company successfully created!'),
      requestFunction: createCompany,
      requestProps: values as ICompany,
    })
    if (response) {
      await handleSelectCompany(response as ICompany)
    }
  }
  const handleSelectCompany = async (value: ICompany) => {
    formik.setFieldValue('company', value)
    await handleCloseCompany()
  }
  const handleCloseCompany = async () => {
    await mutate()
    setCompanyModal(false)
  }

  const isIdentityInvalid = useMemo(() => {
    return currentTab === 1 && formik.values.identity === null
  }, [formik.values.identity, currentTab])

  const isInformationInvalid = useMemo(() => {
    const errorKeys = Object.keys(formik.errors)
    const touchedKeys = Object.keys(formik.touched).filter(
      (key) => formik.touched[key as keyof IUser] === true,
    )

    const isInformationTouched = touchedKeys.some((i) => i !== 'identity')
    const isInformationError = errorKeys.some((i) => i !== 'identity')

    return isInformationTouched && isInformationError
  }, [formik.errors, formik.touched])

  return (
    <S.Wrapper>
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        variant='fullWidth'
        centered
      >
        <Tab
          label={t('Identity')}
          icon={isIdentityInvalid ? <Error color='warning' /> : <></>}
          iconPosition='end'
        />
        <Tab
          label={t('Information')}
          icon={isInformationInvalid ? <Error color='warning' /> : <></>}
          iconPosition='end'
        />
      </Tabs>
      <SwipeableViews index={currentTab} onChangeIndex={(newTab) => setCurrentTab(newTab)}>
        <Box component='div' hidden={currentTab !== 0}>
          <IdentitySelector
            currentIdentity={formik.values.identity as IIdentity}
            onChangeIdentity={(newIdentity, isNew, newPassword) => {
              formik.setFieldValue('is_identity_new', Boolean(isNew))
              if (isNew) formik.setFieldValue('new_password', newPassword)

              formik.setFieldValue('identity', newIdentity)
              if (!isEditing) {
                setTimeout(() => {
                  setCurrentTab(1)
                }, 500)
              }
            }}
            onTouch={() => formik.setFieldTouched('identity', true)}
          />
        </Box>
        <Box component='div' hidden={currentTab !== 1}>
          <FormControl sx={{ my: 2 }} fullWidth>
            <InputLabel id='profile-label'>{t('Profile')} *</InputLabel>
            <Select
              labelId='profile-label'
              id='profile'
              name='profile'
              value={createGuestOnly ? 'guest' : formik.values.profile}
              label={`${t('Profile')} *`}
              placeholder={`${t('Profile')} *`}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {createGuestOnly ? (
                <MenuItem key='guest' value='guest'>
                  {t('Guest')}
                </MenuItem>
              ) : (
                profileOptions.map((profile) => {
                  if (profile.unassignable) return

                  return (
                    <MenuItem key={profile.value} value={profile.value}>
                      {t(profile.label)}
                    </MenuItem>
                  )
                })
              )}
            </Select>
            {Boolean(formik.errors.profile) && (
              <Typography color='error'>{formik.errors.profile}</Typography>
            )}
          </FormControl>

          <ProtectedComponent requiredPermission='list_company'>
            <Field name='company'>
              {(props: FieldProps) => (
                <VinciAutocomplete
                  sx={{ my: 2 }}
                  label={`${t('Company')} *`}
                  options={data}
                  primaryIdentifier={'name'}
                  handleBlur={formik.handleBlur}
                  onCreate={(value: string) => setCompanyModal(value)}
                  onSelect={handleSelectCompany}
                  disabled={loading}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                  renderOption={(props, rawOption) => {
                    const option = rawOption as ICompany

                    return (
                      <Box component='li' {...props} key={option._id}>
                        <Box>
                          <Typography>{option.name}</Typography>
                          <Typography variant='caption'>
                            Parent: {option.parent ?? 'None'}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  }}
                  {...props}
                />
              )}
            </Field>
            {Boolean(formik.errors.company) && (
              <Typography color='error' fontSize={'small'} sx={{ mb: 2 }}>
                {formik.errors.company}
              </Typography>
            )}

            <VinciFormModal
              form={{
                initialValues: {
                  name: companyModal || '',
                  company_type: ['customer'],
                } as ICompany,
                onSubmit: handleSaveCompany,
                validationSchema: companyValidationSchema,
                validateOnChange: true,
              }}
              opened={Boolean(companyModal)}
              onClose={handleCloseCompany}
              title={t('Create Company')}
            >
              <CompanyForm />
            </VinciFormModal>
          </ProtectedComponent>

          {/* <FormControl sx={{ my: 2 }} fullWidth>
            <Field
              component={VinciPhoneInput}
              name='cellphone_number'
              label={`${t('Cellphone Number')}`}
              fullWidth
            />
          </FormControl> */}

          <FormControl sx={{ my: 2 }} fullWidth>
            <InputLabel id='profile-label'>{t('Status')}</InputLabel>
            <Select
              labelId='status-label'
              id='status'
              name='status'
              value={formik.values.status}
              label={`${t('Status')} *`}
              placeholder={`${t('Status')} *`}
              disabled={loggedInUser ? !checkIsManager(loggedInUser) : false}
              onChange={formik.handleChange}
            >
              {statusOptions.map((status) => {
                return (
                  <MenuItem key={status.value} value={status.value}>
                    {t(status.label)}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      </SwipeableViews>
    </S.Wrapper>
  )
}
